import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_PRODUCTS = [
  { id: "tomato-chutney-with-garlic", name: "Tomato Chutney with Garlic", price: 299, image: "https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/zcldzaps_WhatsApp%20Image%202026-07-03%20at%209.16.14%20PM.jpeg" },
  { id: "tomato-chutney-without-garlic", name: "Tomato Chutney without Garlic", price: 299, image: "https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/4ztqw3xw_Screenshot%202026-07-03%20212642.png" },
  { id: "amla-tomato-chutney-with-garlic", name: "Amla Tomato Chutney with Garlic", price: 299, image: "https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/zkjunulw_WhatsApp%20Image%202026-07-03%20at%205.21.14%20PM.jpeg" },
  { id: "amla-tomato-chutney-without-garlic", name: "Amla Tomato Chutney without Garlic", price: 299, image: "https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/9j748fh9_Screenshot%202026-07-03%20212707.png" },
];

export default function OrderDialog({ open, onOpenChange, business }) {
  const { items, subtotal, clear, addItem } = useCart();
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    address: "",
    notes: "",
    single_product_id: "",
    single_qty: 1,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setProducts(r.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) {
      setTimeout(() => setSuccess(null), 200);
    }
  }, [open]);

  const orderItems = useMemo(() => {
    if (items.length > 0) return items;
    if (form.single_product_id) {
      const p = products.find((x) => x.id === form.single_product_id);
      if (p) {
        return [
          {
            id: p.id,
            name: p.name,
            price: p.price,
            image: p.image,
            quantity: Math.max(1, Number(form.single_qty) || 1),
          },
        ];
      }
    }
    return [];
  }, [items, form.single_product_id, form.single_qty, products]);

  const total = useMemo(
    () => orderItems.reduce((a, i) => a + i.price * i.quantity, 0),
    [orderItems]
  );

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildWhatsAppMessage = (order, orderId) => {
    const lines = [
      "🌿 *New Halli Chutney Order*",
      `Order #${(orderId || "").slice(0, 8).toUpperCase()}`,
      "",
      `👤 ${order.customer_name}`,
      `📞 ${order.phone}`,
      `📍 ${order.address}`,
      order.notes ? `📝 ${order.notes}` : null,
      "",
      "*Items:*",
      ...order.items.map(
        (i) => `• ${i.name} × ${i.quantity} — ₹${i.price * i.quantity}`
      ),
      "",
      `*Total: ₹${total}*`,
    ].filter(Boolean);
    return encodeURIComponent(lines.join("\n"));
  };

  const openWhatsApp = (order, orderId) => {
    const num = (business.whatsapp || "").replace(/[^\d]/g, "");
    if (!num) return;
    const url = `https://wa.me/${num}?text=${buildWhatsAppMessage(order, orderId)}`;
    window.open(url, "_blank", "noopener");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (orderItems.length === 0) {
      toast.error("Please pick a product first.");
      return;
    }
    const totalQty = orderItems.reduce((a, i) => a + i.quantity, 0);
    if (totalQty > 20) {
      toast.error("Maximum 20 jars per order. Please reduce quantity.");
      return;
    }
    if (!form.customer_name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error("Please fill name, phone and address.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        items: orderItems.map((i) => ({
          product_id: i.id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
      };
      const res = await axios.post(`${API}/orders`, payload);
      const orderId = res.data.order_id;
      setSuccess({
        orderId,
        emailSent: res.data.email_sent,
      });
      openWhatsApp(payload, orderId);
      clear();
      toast.success("Order placed! We'll confirm on WhatsApp shortly.");
    } catch (err) {
      let msg = "Order failed. Please try again.";
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        const field = detail[0].loc?.slice(-1)[0] || "field";
        msg = `${field}: ${detail[0].msg || "invalid"}`;
      } else if (err?.message) {
        msg = err.message;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSuccess(null);
    setForm({
      customer_name: "",
      phone: "",
      address: "",
      notes: "",
      single_product_id: "",
      single_qty: 1,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-stone-50 border-stone-200 max-h-[92vh] overflow-y-auto"
        data-testid="order-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">
            {success ? "Order Received" : "Place Your Order"}
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            {success
              ? "We'll confirm delivery on WhatsApp shortly."
              : "Fill in the details and we'll take it from here."}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-4 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#2F4F2F]/10 grid place-items-center text-[#2F4F2F]">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div className="mt-4 font-serif text-xl text-stone-900" data-testid="order-success-id">
              Order #{success.orderId.slice(0, 8).toUpperCase()}
            </div>
            <p className="mt-2 text-sm text-stone-600">
              We&apos;ve received your order and {" "}
              {success.emailSent ? "notified our kitchen" : "logged it in our system"}.
              A WhatsApp window has opened so you can send the details directly to us.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-[#993311] hover:bg-[#7A290E] text-stone-50 rounded-full h-11"
                data-testid="order-close-btn"
              >
                Continue Browsing
              </Button>
              <button
                onClick={handleReset}
                className="text-sm text-stone-500 hover:text-[#993311]"
              >
                Place another order
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="order-form">
            {items.length === 0 && (
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label htmlFor="product">Product</Label>
                  <Select
                    value={form.single_product_id}
                    onValueChange={(v) => setField("single_product_id", v)}
                  >
                    <SelectTrigger
                      id="product"
                      className="mt-1.5 bg-white border-stone-300"
                      data-testid="order-product-select"
                    >
                      <SelectValue placeholder="Choose a chutney" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} — ₹{p.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="qty">Quantity</Label>
                  <Input
                    id="qty"
                    type="number"
                    min={1}
                    max={20}
                    value={form.single_qty}
                    onChange={(e) => setField("single_qty", e.target.value)}
                    className="mt-1.5 bg-white border-stone-300"
                    data-testid="order-qty-input"
                  />
                </div>
              </div>
            )}

            {items.length > 0 && (
              <div className="bg-white border border-stone-200 rounded-xl p-3 text-sm">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">
                  Your basket
                </div>
                <ul className="space-y-1">
                  {items.map((i) => (
                    <li key={i.id} className="flex justify-between text-stone-700">
                      <span>{i.name} × {i.quantity}</span>
                      <span className="font-medium">₹{i.price * i.quantity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <Label htmlFor="customer_name">Your Name</Label>
              <Input
                id="customer_name"
                value={form.customer_name}
                onChange={(e) => setField("customer_name", e.target.value)}
                placeholder="e.g., Priya S."
                className="mt-1.5 bg-white border-stone-300"
                data-testid="order-name-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                placeholder="+91 98765 43210"
                className="mt-1.5 bg-white border-stone-300"
                data-testid="order-phone-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="address">Delivery Address</Label>
              <Textarea
                id="address"
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                placeholder="Flat / Street / Area / City / Pincode"
                className="mt-1.5 bg-white border-stone-300"
                rows={3}
                data-testid="order-address-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder="Any preferences or a preferred delivery time?"
                className="mt-1.5 bg-white border-stone-300"
                rows={2}
                data-testid="order-notes-input"
              />
            </div>

            <div className="flex items-center justify-between border-t border-stone-200 pt-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500">
                  Total
                </div>
                <div
                  className="font-serif text-2xl text-[#993311]"
                  data-testid="order-total"
                >
                  ₹{total}
                </div>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#993311] hover:bg-[#7A290E] text-stone-50 rounded-full h-12 px-6 gap-2"
                data-testid="order-form-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Placing...
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-4 h-4" />
                    Place & Send on WhatsApp
                  </>
                )}
              </Button>
            </div>
            <p className="text-[11px] text-stone-500">
              By placing this order you agree that we may contact you on the phone
              number provided to confirm delivery.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
